import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  query,
  where,
  orderBy,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { Reserva } from '../models/reserva.model';

export interface CrearReservaPayload {
  residencialId:    string;
  espacioId:        string;
  fechaInicio:      string;  // ISO UTC — la Cloud Function convierte a Timestamp
  fechaFin:         string;  // ISO UTC
  numeroInvitados?: number;
  comentarios?:     string;
}

export interface CrearReservaResult {
  reservaId: string;
  estado:    string;
}

export interface AprobarReservaPayload {
  residencialId: string;
  reservaId:     string;
}

export interface RechazarReservaPayload {
  residencialId:  string;
  reservaId:      string;
  motivoRechazo?: string;
}

export interface CancelarReservaPayload {
  residencialId: string;
  reservaId:     string;
}

@Injectable({ providedIn: 'root' })
export class ReservaService {
  private firestore  = inject(Firestore);
  private functions  = inject(Functions);

  private col(residencialId: string) {
    return collection(this.firestore, `residenciales/${residencialId}/reservas`);
  }

  /** Listener en tiempo real de toda la colección. Los splits por estado van en computed() en la página dueña. */
  getReservas(residencialId: string): Observable<Reserva[]> {
    return collectionData(this.col(residencialId), { idField: 'id' }) as Observable<Reserva[]>;
  }

  /** Listener de todas las reservas activas del residente, ordenadas por fecha desc. */
  getReservasByResidente(residencialId: string, residenteUid: string): Observable<Reserva[]> {
    const q = query(
      this.col(residencialId),
      where('residenteUid', '==', residenteUid),
      where('activo',       '==', true),
      orderBy('fechaInicio', 'desc')
    );
    return collectionData(q, { idField: 'id' }) as Observable<Reserva[]>;
  }

  /** Listener acotado a un espacio específico: solo reservas pendientes/aprobadas y activas. */
  getReservasActivasByEspacio(residencialId: string, espacioId: string): Observable<Reserva[]> {
    const q = query(
      this.col(residencialId),
      where('espacioId', '==', espacioId),
      where('activo',    '==', true),
      where('estado',    'in', ['pendiente', 'aprobada'])
    );
    return collectionData(q, { idField: 'id' }) as Observable<Reserva[]>;
  }

  async crearReserva(payload: CrearReservaPayload): Promise<CrearReservaResult> {
    const fn = httpsCallable<CrearReservaPayload, CrearReservaResult>(
      this.functions, 'crearReserva'
    );
    const result = await fn(payload);
    return result.data;
  }

  async aprobarReserva(payload: AprobarReservaPayload): Promise<void> {
    const fn = httpsCallable<AprobarReservaPayload>(this.functions, 'aprobarReserva');
    await fn(payload);
  }

  async rechazarReserva(payload: RechazarReservaPayload): Promise<void> {
    const fn = httpsCallable<RechazarReservaPayload>(this.functions, 'rechazarReserva');
    await fn(payload);
  }

  async cancelarReserva(payload: CancelarReservaPayload): Promise<void> {
    const fn = httpsCallable<CancelarReservaPayload>(this.functions, 'cancelarReserva');
    await fn(payload);
  }
}
